import { createApi } from '@reduxjs/toolkit/query/react';
import { apiAuthenticatedQuery, internalHeaders } from './baseQuery';
import {
  getUserResponse,
  getUsersRequest,
  getUsersResponse,
  postUserSendEmailChangeMail,
  putUserEmailRequest,
  putUserNameRequest,
  putUserPasswordRequest,
} from '../types/user';
import { extinctFalsyFromParams } from './nullExtict';
import { errorTagTypes, handleErrorTags } from './handleErrorTags';

export const userApi = createApi({
  baseQuery: apiAuthenticatedQuery,
  reducerPath: 'user',
  tagTypes: ['User', ...errorTagTypes],
  endpoints: (build) => ({
    getUsersWithoutLoading: build.query<getUsersResponse, getUsersRequest>({
      query: (data) => ({
        url: `/users`,
        params: extinctFalsyFromParams(data || {}),
        headers: internalHeaders({ loading: 'suppress' }),
      }),
      providesTags: (_res, error) => handleErrorTags(error),
    }),
    getCurrentUser: build.query<getUserResponse, void>({
      query: () => ({
        url: `/user`,
      }),
      providesTags: (_res, error) => [
        { type: 'User', id: 'CurrentUser' },
        ...handleErrorTags(error),
      ],
    }),
    putUserName: build.mutation<void, putUserNameRequest>({
      query: (request) => ({
        url: `/user/name`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: [{ type: 'User', id: 'CurrentUser' }],
    }),
    postUserSendEmailChangeMail: build.mutation<
      void,
      postUserSendEmailChangeMail
    >({
      query: (request) => ({
        url: `/user/send-email-change-mail`,
        method: 'POST',
        body: request,
      }),
    }),
    putUserEmail: build.mutation<void, putUserEmailRequest>({
      query: (request) => ({
        url: `/user/email`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: [{ type: 'User', id: 'CurrentUser' }],
    }),
    putUserPassword: build.mutation<void, putUserPasswordRequest>({
      query: (request) => ({
        url: `/user/password`,
        method: 'PUT',
        body: request,
      }),
    }),
  }),
});

export const {
  useGetUsersWithoutLoadingQuery,
  useGetCurrentUserQuery,
  usePutUserNameMutation,
  usePostUserSendEmailChangeMailMutation,
  usePutUserEmailMutation,
  usePutUserPasswordMutation,
} = userApi;
